<template>
  <vl-region>
    <vl-layout>
      <PbsGoBackLink />
      <vl-grid>
        <vl-column width="7" width-xs="12">
          <vl-title tag-name="h1">
            {{ $t('deliveryprocessdefinition.geckoSync.title') }}
          </vl-title>
        </vl-column>
        <vl-column v-if="loadingState.mounted" width="5" width-xs="12">
          <vl-action-group mod-align-right mod-space between>
            <PbsActionButton :label="$t('general.moreActions')" :items="actionButtonItems" mod-large> </PbsActionButton>
          </vl-action-group>
        </vl-column>
        <vl-column v-else-if="loadingState.loading" width="12" width-xs="12">
          <vl-region>
            <div v-vl-align:center>
              <vl-loader :message="$t('general.pageLoading')" />
            </div>
          </vl-region>
        </vl-column>
        <vl-column v-if="formState.hasChanges" v-vl-spacer:bottom.medium>
          <vl-alert icon="warning" title="Opgelet!" mod-warning>
            <p>
              {{ t('deliveryprocessdefinition.geckoSync.configurationChanged') }}
              <vl-link @click="showConfirmSyncModal">{{
                t('deliveryprocessdefinition.geckoSync.uploadToGecko')
              }}</vl-link>
            </p>
          </vl-alert>
        </vl-column>
      </vl-grid>
      <template v-if="loadingState.mounted">
        <vl-grid>
          <vl-column width="12" width-xs="12">
            <vl-grid v-if="deliverySpecification">
              <vl-column width="12" width-xs="12">
                <vl-title tag-name="h2">
                  {{ $t('deliveryprocessdefinition.geckoSync.deliverySpecification') }}
                  <PbsStatusIcon v-if="detail" :id="detail?.statusId" />
                </vl-title>
              </vl-column>
            </vl-grid>
          </vl-column>
          <vl-column width="12" width-xs="12">
            <vl-properties-list>
              <vl-properties-label>{{ $t('deliveryspecification.detail.id') }}</vl-properties-label>
              <vl-properties-data>{{ deliverySpecification.id }}</vl-properties-data>
              <vl-properties-label>{{ $t('deliveryspecification.detail.label') }}</vl-properties-label>
              <vl-properties-data>{{ deliverySpecification.label }}</vl-properties-data>
              <vl-properties-label>{{
                $t('deliveryprocessdefinition.geckoSync.definitionStatus')
              }}</vl-properties-label>
              <vl-properties-data>{{ statusLabel }}</vl-properties-data>
              <vl-properties-label>{{ $t('deliveryprocessdefinition.geckoSync.qualityCheck') }}</vl-properties-label>
              <vl-properties-data>
                <vl-checkbox
                  v-if="canChangeQualityCheck"
                  v-model="formState.qualityCheck"
                  mod-switch
                  :mod-disabled="!canChangeQualityCheck"
                  @click="onQualityCheckStatusChanged"
                >
                  {{ formState.qualityCheck ? $t('general.active') : $t('general.inactive') }}</vl-checkbox
                >
                <span v-else>
                  {{ formState.qualityCheck ? $t('general.active') : $t('general.inactive') }}
                </span>
              </vl-properties-data>
            </vl-properties-list>
          </vl-column>
        </vl-grid>
        <vl-grid v-vl-spacer:bottom.medium v-vl-spacer:top.medium>
          <vl-column width="12" width-xs="12">
            <vl-title tag-name="h2">{{ $t('deliveryprocessdefinition.geckoSync.geckoSpecification') }}</vl-title>
            <template v-if="geckoSpecificationLoading">
              <vl-loader mod-inline :message="$t('deliveryprocessdefinition.geckoSync.specificationLoading')" />
            </template>
            <vl-column v-else-if="geckoSpecification" width="12" width-xs="12">
              <GeckoSpecificationRead :detail="geckoSpecification" />
            </vl-column>
            <vl-column v-else-if="geckoSpecificationNotFound" v-vl-spacer:bottom.medium width="12" width-xs="12">
              <vl-alert icon="warning" mod-naked>
                <span class="vl-u-mark--accent">
                  {{ t('deliveryprocessdefinition.geckoSync.geckoSpecificationRequired') }}
                </span>
              </vl-alert>
            </vl-column>
          </vl-column>
        </vl-grid>
        <GeckoTestDefaultTests
          v-if="dataState.defaultTests && dataState.defaultTests.length > 0"
          :defaultTests="dataState.defaultTests"
          :isReadOnly="!canChangeQualityCheck"
          :onCheckboxChange="saveBasicQualityCheckStatus"
        />
        <GeckoTestStandardTests
          v-if="deliveryEntities && deliveryEntities.length > 0"
          :standardTestTypes="dataState.standardTestTypes"
          :standardTests="dataState.standardTests"
          :deliveryEntities="deliveryEntities"
          :isReadOnly="!canChangeQualityCheck"
          :onCheckboxChange="saveStandardQualityCheckStatus"
          :onAfterChange="loadStandardTests"
          :onFormSubmit="saveDeliveryQualityCheck"
          :onDeleteTest="deleteDeliveryQualityCheck"
        />
      </template>
    </vl-layout>
  </vl-region>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import { computed, onMounted, onUnmounted, reactive, watch } from 'vue';
import { onBeforeRouteLeave, useRoute } from 'vue-router';
import { useDeliveryProcessDefinitionDetailStore } from '../../store/delivery-process-definition/delivery-process-definition-detail.store';
import { useGeckoSpecificationDetailStore } from '../../store/gecko-specification/gecko-specification-detail.store';
import { useGeckoSynchronisationStore } from '../../store/gecko-synchronisation/gecko-synchronisation.store';
import {
  DeleteQualityCheckDto,
  DeliveryProcessDefinitionDetailDto,
  GeckoStandardTestTypeDto,
  GeckoTestDto,
  GeckoTestFunction,
  SaveBasicQualityCheckDto,
  SaveStandardQualityCheckDto,
  SaveStandardQualityCheckStatusDto
} from '@/api/backoffice/clients';
import GeckoSpecificationRead from './subcomponents/gecko-specification/GeckoSpecificationRead.vue';
import { DeliveryQualityCheckData } from './subcomponents/gecko-test-datatable/GeckoTestActiveToggle.vue';
import GeckoTestDefaultTests from './subcomponents/gecko-test-default-tests/GeckoTestDefaultTests.vue';
import GeckoTestStandardTests from './subcomponents/gecko-test-standard-tests/GeckoTestStandardTests.vue';
import PbsGoBackLink from '@/components/PbsGoBackLink.vue';
import PbsActionButton, { PbsActionButtonItem } from '@/components/PbsActionButton.vue';
import PbsStatusIcon from '@/components/PbsStatusIcon.vue';
import { useDeliverySpecificationStore } from '../../store/delivery-specification/delivery-specification.store';
import { useModalStore } from '@/modules/core/store/modal.store';
import { useDeliveryEntityStore } from '../../store/delivery-entity/delivery-entity.store';
import { useDeliveryQualityCheckStore } from '../../store/delivery-quality-check/delivery-quality-check.store';
import { useAlertStore } from '@/modules/core/store/alert.store';
import { ENUM_StatusTypes } from '@/modules/core/common';
import DefinitionStatusForm from './subcomponents/definition-status-form/DefinitionStatusForm.vue';
import { DeliveryProcessDefinitionActionButtonType } from '../../common/delivery-process-definition.common';
import { ENUM_DeliveryQualityCheckTypes } from '../../common';
import { GeckoStandardTestFormDto } from './subcomponents/gecko-test-standard-tests/GeckoStandardTestForm.vue';

const definitionDetailStore = useDeliveryProcessDefinitionDetailStore();
const deliverySpecificationStore = useDeliverySpecificationStore();
const deliveryEntityStore = useDeliveryEntityStore();
const geckoSpecificationStore = useGeckoSpecificationDetailStore();
const deliveryQualityCheckStore = useDeliveryQualityCheckStore();
const geckoSynchronisationStore = useGeckoSynchronisationStore();
const modalStore = useModalStore();

const { t } = useI18n();
const route = useRoute();

const loadingState = reactive({
  loading: false,
  saving: false,
  mounted: false
});

const dataState = reactive({
  defaultTests: [] as GeckoTestDto[],
  standardTests: [] as GeckoTestDto[],
  standardTestTypes: [] as GeckoStandardTestTypeDto[]
});

const formState = reactive({
  qualityCheck: false,
  hasChanges: false
});

const actionButtonItems = computed((): PbsActionButtonItem[] => {
  return [
    {
      name: DeliveryProcessDefinitionActionButtonType.ChangeStatus,
      label: t('deliveryprocessdefinition.actions.changeStatus.label'),
      click: showChangeStatusModal,
      ellipsis: true
    },
    {
      label: t('deliveryprocessdefinition.geckoSync.actions.exportToJson.label'),
      click: exportToJson
    },
    {
      label: t('deliveryprocessdefinition.geckoSync.actions.sync.label'),
      click: showConfirmSyncModal,
      ellipsis: true
    }
  ];
});

const detail = computed((): DeliveryProcessDefinitionDetailDto => {
  return definitionDetailStore.detail;
});

const deliverySpecification = computed(() => {
  return deliverySpecificationStore.byDefinition(id.value)[0];
});

const deliveryEntities = computed(() => {
  return deliveryEntityStore.byDefinition(id.value);
});

const geckoSpecification = computed(() => {
  return geckoSpecificationStore.detail;
});

const geckoSpecificationLoading = computed((): boolean => {
  return geckoSpecificationStore.loading;
});

const geckoSpecificationNotFound = computed((): boolean => {
  return geckoSpecificationStore.notFound;
});

const id = computed((): number => {
  return parseInt(route.params.id as string, 10);
});

const canChangeQualityCheck = computed((): boolean => {
  const isDraft = detail.value?.statusId === ENUM_StatusTypes.Draft.id;
  return isDraft;
});

watch(
  () => deliverySpecification.value,
  (newSpec) => {
    if (newSpec && !geckoSpecification.value) {
      getGeckoSpecification();
    }
  }
);

const statusLabel = computed((): string => {
  return Object.values(ENUM_StatusTypes).find((status) => status.id === detail.value?.statusId)?.name ?? '-';
});

const saveBasicQualityCheckStatus = async (deliveryQualityCheckData: DeliveryQualityCheckData): Promise<boolean> => {
  const saveDto: SaveBasicQualityCheckDto = {
    code: deliveryQualityCheckData.test.code,
    testFunction: deliveryQualityCheckData.test.function,
    isEnabled: deliveryQualityCheckData.isEnabled
  };

  const success = await deliveryQualityCheckStore.saveBasic(deliverySpecification.value.id, saveDto);

  if (success) {
    formState.hasChanges = true;
  }

  return success;
};

const saveStandardQualityCheckStatus = async (deliveryQualityCheckData: DeliveryQualityCheckData): Promise<boolean> => {
  const saveDto: SaveStandardQualityCheckStatusDto = {
    code: deliveryQualityCheckData.test.code,
    isEnabled: deliveryQualityCheckData.isEnabled
  };

  const success = await deliveryQualityCheckStore.saveStandardStatus(deliverySpecification.value.id, saveDto);

  if (success) {
    formState.hasChanges = true;
  }

  return success;
};

const deleteDeliveryQualityCheck = async (dto: GeckoTestDto): Promise<boolean> => {
  const deleteDto = {
    code: dto.test.code,
    qualityCheckType: ENUM_DeliveryQualityCheckTypes.Standaardtesten
  } as DeleteQualityCheckDto;

  const success = await deliveryQualityCheckStore.delete(deliverySpecification.value.id, deleteDto);

  if (success) {
    formState.hasChanges = true;
  }

  return success;
};

const saveDeliveryQualityCheck = async (dto: GeckoStandardTestFormDto): Promise<boolean> => {
  const standardTestType = geckoSynchronisationStore.getStandardTestTypeById(dto.testType.id);

  const code = `${standardTestType.id}_${dto.entity.code}`;
  const testFunction = GeckoTestFunction[standardTestType.testFunction];
  const deliveryEntityId = dto.entity.id;

  let parameters: { [key: string]: string } = null;
  dto.parameters?.forEach((p) => {
    if (!parameters) {
      parameters = {};
    }
    parameters[p.key] = p.value;
  });

  const saveDto: SaveStandardQualityCheckDto = {
    code,
    testFunction,
    deliveryEntityId,
    parameters
  };

  const success = dto.isEditing
    ? await deliveryQualityCheckStore.updateStandard(deliverySpecification.value.id, saveDto)
    : await deliveryQualityCheckStore.insertStandard(deliverySpecification.value.id, saveDto);

  if (success) {
    formState.hasChanges = true;
  }

  return success;
};

const showChangeStatusModal = () => {
  const modalContent = {
    title: t('deliveryprocessdefinition.actions.changeStatus.description'),
    component: DefinitionStatusForm,
    props: {
      id: detail.value.id
    }
  };

  modalStore.showModal(modalContent);
};

const onQualityCheckStatusChanged = () => {
  if (!canChangeQualityCheck.value) {
    return;
  }

  const newValue = !formState.qualityCheck;
  if (newValue && !geckoSpecification.value) {
    useAlertStore().setError(t('deliveryprocessdefinition.geckoSync.geckoSpecificationRequired'));
    setTimeout(() => {
      formState.qualityCheck = false;
    }, 200);

    return;
  }

  if (deliverySpecification.value) {
    deliverySpecificationStore.partialUpdate(detail.value.id, deliverySpecification.value.id, {
      skipCheck: !newValue
    });
  }
};

const exportToJson = () => {
  geckoSynchronisationStore.geckoExport(id.value);
};

const getGeckoSpecification = () => {
  if (deliverySpecification.value) {
    const specificationCode = `DP${deliverySpecification.value.id}`;
    geckoSpecificationStore.search(specificationCode);
  }
};

const geckoSync = async () => {
  loadingState.saving = true;
  const succeeded = await geckoSynchronisationStore.geckoSync(id.value);
  loadingState.saving = false;
  if (succeeded) {
    formState.hasChanges = false;
    getGeckoSpecification();
  }
  return succeeded;
};

const showConfirmSyncModal = () => {
  modalStore.showConfirmModal({
    title: t('deliveryprocessdefinition.geckoSync.actions.sync.label'),
    props: {
      confirmationMessage: t('deliveryprocessdefinition.geckoSync.confirmationMessage'),
      okLabel: t('deliveryprocessdefinition.geckoSync.confirmLabel'),
      confirm: () => geckoSync()
    }
  });
};

const loadDefaultTests = async () => {
  dataState.defaultTests = await geckoSynchronisationStore.getGeckoDefaultTests(id.value);
};

const loadStandardTests = async () => {
  dataState.standardTests = await geckoSynchronisationStore.getGeckoStandardTests(id.value);
};

const loadStandardTestTypes = async () => {
  dataState.standardTestTypes = await geckoSynchronisationStore.getGeckoStandardTestTypes();
};

const loadDeliveryProcessDefinition = async () => {
  await definitionDetailStore.get(id.value);
};

const loadDeliverySpecification = async () => {
  await deliverySpecificationStore.getByDefinition(id.value);
};

const loadDeliveryEntities = async () => {
  await deliveryEntityStore.getByDefinition(id.value);
};

const clearStores = () => {
  if (deliverySpecification.value) {
    deliveryEntityStore.clear(deliverySpecification.value.id);
  }
  deliverySpecificationStore.clear(id.value);
  definitionDetailStore.clear(id.value);
  geckoSpecificationStore.clear();
};

onBeforeRouteLeave((to, from, next) => {
  if (formState.hasChanges) {
    modalStore.showConfirmModal({
      title: t('deliveryprocessdefinition.geckoSync.title'),
      props: {
        confirmationMessage: t('deliveryprocessdefinition.geckoSync.unsavedChangesMessage'),
        okLabel: t('general.leavePage'),
        cancelLabel: t('general.cancel'),
        confirm: async () => {
          next();
          return true;
        }
      }
    });
  } else {
    next();
  }
});

onMounted(async () => {
  try {
    loadingState.loading = true;
    await Promise.all([
      loadDefaultTests(),
      loadStandardTests(),
      loadStandardTestTypes(),
      loadDeliveryProcessDefinition(),
      loadDeliverySpecification(),
      loadDeliveryEntities()
    ]);
  } finally {
    loadingState.loading = false;
    loadingState.mounted = true;
    formState.qualityCheck = deliverySpecification.value ? !deliverySpecification.value.skipCheck : false;
  }
});

onUnmounted(() => {
  clearStores();
});
</script>

<style lang="scss" scoped>
.with-dots {
  list-style-type: disc;
  padding-left: 2rem;
}
</style>
